import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import {
  Box,
  Button,
  Heading,
  Text,
  Stack,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Flex,
  Image,
  useColorModeValue,
  ScaleFade
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { GetUserInfo, UserModel } from "../../../services/Login/LoginService";
import { useAppContext } from "../../../context/AppContext";
import { FaMicrosoft } from "react-icons/fa";

const pulse = `
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const Login: React.FC = () => {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const { setUserData, userData } = useAppContext();
  const scope = process.env.REACT_APP_OUTLOOK_API_SCOPE;
  const [userInfo, setUserInfo] = useState<UserModel | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [accessDenied, setAccessDenied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const bgColor = useColorModeValue("white", "gray.800");
  const pulseAnimation = `${pulse} 2s infinite`;

  if (!scope) {
    console.error(
      "Error: REACT_APP_OUTLOOK_API_SCOPE is not defined. Ensure the environment variable is set."
    );
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Text color="red.500" fontSize="lg">
          Configuration error: Missing API scope. Please contact support.
        </Text>
      </Box>
    );
  }

  const handleLogin = async () => {
    try {
      setIsLoading(true);
      const loginResponse = await instance.loginPopup({
        scopes: [scope],
      });
      const accessToken = loginResponse.accessToken;
      const email = loginResponse.account?.username;

      if (!email) {
        console.error("Login failed: Unable to retrieve user email.");
        setIsLoading(false);
        return;
      }

      const userInfo = await GetUserInfo(accessToken, email);
      if (!userInfo) {
        onOpen(); // Show Access Denied
        return;
      }
      setUserData(userInfo);
      setUserInfo(userInfo);
      if (userInfo && (userInfo.roleID === 1 || userInfo.roleID == 3)) {
        console.log("Login successful");
        navigate("/dashboard");
      } else {
        onOpen();
      }
    } catch (error) {
      console.error("Login failed: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bgGradient="linear(to-r, blue.500, purple.600)"
      position="relative"
      overflow="hidden"
    >
      {/* Animated background elements */}
      <Box
        position="absolute"
        top="-50px"
        left="-50px"
        w="200px"
        h="200px"
        borderRadius="full"
        bg="rgba(255,255,255,0.1)"
        filter="blur(40px)"
      />
      <Box
        position="absolute"
        bottom="-100px"
        right="-100px"
        w="300px"
        h="300px"
        borderRadius="full"
        bg="rgba(255,255,255,0.1)"
        filter="blur(60px)"
      />

      <ScaleFade in initialScale={0.9}>
        <Box
          bg={bgColor}
          p={10}
          rounded="xl"
          shadow="2xl"
          w="full"
          maxW="md"
          mx={4}
          position="relative"
          zIndex="1"
          borderWidth="1px"
          borderColor="rgba(255,255,255,0.2)"
        >
          <Flex justify="center" mb={8}>
            <Image
              src="https://upload.wikimedia.org/wikipedia/commons/9/96/Microsoft_logo_%282012%29.svg"
              alt="Microsoft Logo"
              w="180px"
            />
          </Flex>

          <Heading
            as="h1"
            size="xl"
            textAlign="center"
            mb={2}
            bgGradient="linear(to-r, blue.500, purple.600)"
            bgClip="text"
          >
            Welcome Back
          </Heading>

          <Text
            textAlign="center"
            color="gray.600"
            mb={8}
            fontSize="lg"
          >
            Sign in to access your dashboard and manage your account.
          </Text>

          <Stack spacing={4}>
            <Button
              leftIcon={<FaMicrosoft size="24px" />}
              colorScheme="blue"
              size="lg"
              onClick={handleLogin}
              isLoading={isLoading}
              loadingText="Signing in..."
              py={6}
              fontSize="md"
              fontWeight="bold"
              animation={!isLoading ? pulseAnimation : undefined}
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "lg"
              }}
              transition="all 0.2s"
            >
              Continue with Microsoft 365
            </Button>
          </Stack>

          <Text mt={8} textAlign="center" color="gray.500" fontSize="sm">
            By signing in, you agree to our Terms of Service and Privacy Policy.
          </Text>
        </Box>
      </ScaleFade>

      {/* Access Denied Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(5px)" />
        <ModalContent mx={4} borderRadius="xl">
          <ModalHeader>
            <Flex align="center">
              <Box
                mr={3}
                p={2}
                rounded="full"
                bg="red.100"
                color="red.600"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              </Box>
              <Text fontSize="xl" fontWeight="bold">Access Denied</Text>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Text>
              You don't have the required permissions to access this application.
              Please contact your administrator if you believe this is an error.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={() => {
                onClose();
                instance.logoutPopup();
              }}
            >
              Sign Out
            </Button>
            <Button variant="ghost" ml={3} onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default Login;